import 'dayjs/locale/cs.js'
import 'dayjs/locale/en.js'
import 'dayjs/locale/en-gb.js'
import 'dayjs/locale/sk.js'
import 'dayjs/locale/ro.js'
import 'dayjs/locale/de.js'
import 'dayjs/locale/hu.js'
import 'dayjs/locale/it.js'
import 'dayjs/locale/pl.js'
import 'dayjs/locale/uk.js'
import 'dayjs/locale/ru.js'
import 'dayjs/locale/es.js'
import 'dayjs/locale/bg.js'
import 'dayjs/locale/pt.js'
import 'dayjs/locale/fr.js'
import 'dayjs/locale/nl.js'

import { type Plugin } from 'vue'

import isBetween from 'dayjs/plugin/isBetween'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import dayjsModule, { Dayjs, extend, locale, type PluginFunc, Ls } from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useLocale } from '../i18n'

const DATE_FORMAT = 'YYYY-MM-DD'
const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'
const TIME_FORMAT = 'HH:mm'

declare module 'dayjs' {
  function parse(input: string, format: string, timezone?: string): dayjsModule.Dayjs | null
  function parseDate(input: string, timezone?: string): dayjsModule.Dayjs | null
  function parseDateTime(input: string, timezone?: string): dayjsModule.Dayjs | null
  function parseTimeString(input: string, timezone?: string): dayjsModule.Dayjs | null
  function localDate(input: string, timezone?: string): dayjsModule.Dayjs
  function localDateTime(input: string, timezone?: string): dayjsModule.Dayjs

  interface Dayjs {
    withCurrentTime(): dayjsModule.Dayjs
    withTimeFrom(instance: dayjsModule.Dayjs | null): dayjsModule.Dayjs
    isTzToday(): boolean
    toLocalDateString(): string
    toLocalDateTimeString(): string
    toTimeString(): string
    isWeekend(): boolean
  }
}

const withCurrentTime: PluginFunc = (_option, dayjsClass, dayjsFactory) => {
  dayjsClass.prototype.withCurrentTime = function (): Dayjs {
    const now = dayjsFactory()

    return this.hour(now.hour())
      .minute(now.minute())
      .second(now.second())
      .millisecond(now.millisecond())
  }
}

const withTimeFrom: PluginFunc = (_option, dayjsClass, dayjsFactory) => {
  dayjsClass.prototype.withTimeFrom = function (instance: Dayjs | null): Dayjs {
    const source = instance?.isValid() ? instance : dayjsFactory()

    return this.hour(source.hour())
      .minute(source.minute())
      .second(source.second())
      .millisecond(source.millisecond())
  }
}

const isTzToday: PluginFunc = (option, dayjsClass, dayjsFactory) => {
  dayjsClass.prototype.isTzToday = function () {
    const now = dayjsFactory().tz().startOf('day')

    return this.startOf('day').isSame(now, 'date')
  }
}

const isWeekend: PluginFunc = (option, dayjsClass) => {
  dayjsClass.prototype.isWeekend = function () {
    const day = this.day()

    return day === 0 || day === 6
  }
}

const parse: PluginFunc = (option, dayjsClass, dayjsFactory) => {
  dayjsFactory.parse = function (input: string, format?: string, timezone?: string): Dayjs | null {
    // dayjs.tz(date, format, timezone) doesn't work with undefined timezone (= default timezone)
    // so this custom function is a workaround

    const date = dayjsFactory.utc(input, format, true)

    if (!date.isValid()) {
      return null
    }

    return date.tz(timezone, true)
  }
}

const formatHelpers: PluginFunc = (option, dayjsClass, dayjsFactory) => {
  dayjsFactory.parseDate = function (input: string, timezone?: string): Dayjs | null {
    return dayjsFactory.parse(input, DATE_FORMAT, timezone)
  }

  dayjsFactory.parseDateTime = function (input: string, timezone?: string): Dayjs | null {
    return dayjsFactory.parse(input, DATE_TIME_FORMAT, timezone)
  }

  dayjsFactory.parseTimeString = function (input: string, timezone?: string): Dayjs | null {
    return dayjsFactory.parse(input, TIME_FORMAT, timezone)
  }

  dayjsFactory.localDate = function (input: string, timezone?: string): Dayjs {
    return dayjsFactory.parseDate(input, timezone) ?? dayjsFactory(null)
  }

  dayjsFactory.localDateTime = function (input: string, timezone?: string): Dayjs {
    return dayjsFactory.parseDateTime(input, timezone) ?? dayjsFactory(null)
  }

  dayjsClass.prototype.toLocalDateString = function (): string {
    return this.format(DATE_FORMAT)
  }

  dayjsClass.prototype.toLocalDateTimeString = function (): string {
    return this.format(DATE_TIME_FORMAT)
  }

  dayjsClass.prototype.toTimeString = function (): string {
    return this.format(TIME_FORMAT)
  }
}

const overrideLocaleWeekStart: PluginFunc = (): void => {
  Object.keys(Ls).forEach((localeName: string): void => {
    const locale = Ls[localeName]

    if (!locale) {
      return
    }

    locale.weekStart = 1
  })
}

export const configureDayjs: Plugin = {
  install: (): void => {
    extend(utc)
    extend(timezone)
    extend(isBetween)
    extend(isSameOrAfter)
    extend(isSameOrBefore)
    extend(isToday)
    extend(customParseFormat)
    extend(withCurrentTime)
    extend(withTimeFrom)
    extend(isTzToday)
    extend(parse)
    extend(formatHelpers)
    extend(isWeekend)
    extend(overrideLocaleWeekStart)
    locale(useLocale().get())
  },
}
